<template>
  <div :style="`height: ${mx_contentHeight};`">
    <responsive-drawer-layout backgroundImgUrl="">
      <template v-slot:drawer>
        <div v-if="questionnaire && !mx_needsDrawer">
          <drawer-desktop
            @onSelectedTopic="selectTopic"
            v-model="questionnaireData"
            :selectedTopicId="String(selectedTopicId)"
            :questionnaire="questionnaire"
            :evaluationType="evaluationType"
          >
          </drawer-desktop>
        </div>
      </template>
      <!-- CONTENT -->
      <template v-slot:content>
        <transition name="fade">
          <v-alert v-if="showAlert && !currentUser.id" prominent type="error" class="mx-7 mt-7 mb-0">
            <div class="alert-container">
              <p class="font-weight-black">{{ $t('bf.userNotFound') }}</p>
              <v-btn @click="pageHistoryBack"
                ><p class="ma-0 font-weight-black">{{ $t('back') }}</p></v-btn
              >
            </div>
          </v-alert>
        </transition>
        <!-- TOPICS -->
        <div v-if="isSelectedTopicIdNumeric()">
          <div v-if="filteredQuestions" class="pa-7 questions-container" ref="questions-container">
            <section
              v-for="(question, index) in filteredQuestions"
              :class="{
                'mb-7': index < filteredQuestions.length - 1,
                'pa-4 rounded-lg': true,
                'grey lighten-3': !darkMode,
                'grey darken-3': darkMode,
              }"
              :key="`question-id-${question.id}`"
            >
              <p class="body-1">{{ question.title }}</p>
              <p class="body-2">{{ question.text }}</p>
              <!-- Check if answerSets length is more than 5 -->
              <div v-if="answerSetsById[question.answerSetIdFk] === undefined"></div>
              <v-btn-toggle
                @change="updateStoreAndLocalStorage"
                v-model="questionnaireData.topics[selectedTopicId].questions[question.id].answer"
                v-else-if="areFiveOrLessAnswers(question) && mdAndUp"
                class="mb-5"
                active-class="primary"
                :color="textFieldClass"
                rounded
              >
                <v-btn v-if="hasNoRatingOption(question)" :value="'0'">
                  {{ $t('bf.noRating') }}
                </v-btn>
                <v-btn v-for="answer in answerSetsById[question.answerSetIdFk].answers" :key="`answer-id-${answer.id}`" :value="answer.id">
                  {{ answer.answer }}
                </v-btn>
              </v-btn-toggle>
              <v-chip-group
                @change="updateStoreAndLocalStorage"
                v-else
                v-model="questionnaireData.topics[selectedTopicId].questions[question.id].answer"
                class="mb-4"
                active-class="primary darken-1"
                column
              >
                <v-chip v-if="hasNoRatingOption(question)" :value="'0'" :color="textFieldClass" filter>{{ $t('bf.noRating') }}</v-chip>
                <v-chip
                  v-for="answer in answerSetsById[question.answerSetIdFk].answers"
                  :key="`answer-id-${answer.id}`"
                  :value="answer.id"
                  filter
                  :color="textFieldClass"
                >
                  {{ answer.answer }}
                </v-chip>
              </v-chip-group>
              <!-- Else, use v-btn-toggle for 5 or less answers -->

              <p class="body-2" v-if="getDescription(question.id)">{{ getDescription(question.id) }}</p>
              <v-textarea
                @blur="updateStoreAndLocalStorage"
                v-model="questionnaireData.topics[selectedTopicId].questions[question.id].comment"
                class="rounded-lg"
                :background-color="textFieldClass"
                rows="2"
                hide-details="auto"
                no-resize
                auto-grow
                outlined
                :class="{
                  isRequired: additionalInfoIsRequired(question.id),
                }"
              ></v-textarea>

              <!-- Image preview and upload here -->
              <div v-if="answerSetsById[question.answerSetIdFk] === undefined"></div>
              <div v-else-if="canAddMedias(question)">
                <p class="caption mt-3">{{ $t('bf.addImages') }}</p>
                <v-row>
                  <v-col class="d-flex flex-wrap">
                    <div
                      v-if="mx_attachmentMode === attachmentModeOptions.cameraAndGallery || mx_attachmentMode === attachmentModeOptions.cameraOnly"
                      class="file-input-outer-container"
                    >
                      <div class="file-input-container" @click="uploadFile">
                        <v-file-input
                          capture="user"
                          ref="fileInputRef"
                          class="file-input"
                          multiple
                          accept="image/*"
                          :hide-input="true"
                          hide-details
                          @change="addImagePreview(question.id, $event)"
                          prepend-icon="mdi-camera"
                        ></v-file-input>
                      </div>
                    </div>
                    <div
                      v-if="mx_attachmentMode === attachmentModeOptions.cameraAndGallery || mx_attachmentMode === attachmentModeOptions.galleryOnly"
                      class="file-input-outer-container"
                    >
                      <div class="file-input-container" @click="uploadFile">
                        <v-file-input
                          ref="fileInputRef"
                          class="file-input"
                          multiple
                          accept="image/*"
                          :hide-input="true"
                          hide-details
                          @change="addImagePreview(question.id, $event)"
                          :prepend-icon="mx_attachmentMode === attachmentModeOptions.galleryOnly ? 'mdi-paperclip' : 'mdi-image'"
                        ></v-file-input>
                      </div>
                    </div>
                    <div
                      v-for="(img, index) in questionnaireData.topics[selectedTopicId].questions[question.id].images"
                      :key="'img-preview-' + question.id + '-' + index"
                      class="pl-2 pr-2 pb-2 img-preview-container"
                    >
                      <v-img :src="img" class="img-preview" aspect-ratio="1">
                        <v-overlay absolute class="img-overlay" @click="removeImg(question.id, index)" opacity="0">
                          <v-icon class="preview-delete-icon" color="white" small>mdi-window-close</v-icon>
                        </v-overlay>
                      </v-img>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </section>
          </div>
        </div>

        <!-- BASE DATA -->
        <div v-else-if="selectedTopicId === 'basedata'" class="pa-7 basedata-container" ref="basedata-container">
          <v-row v-if="metaFields.length > 0">
            <div v-for="(metaField, index) in metaFields" :key="`meta-field-${index}`" class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ metaField.label }}</p>
                <p v-if="metaField.type == 'prefilled' && prefillData[metaField.value] && !Array.isArray(prefillData[metaField.value])" class="body-1 mb-0">
                  {{ prefillData[metaField.value] }}
                </p>
                <template v-else-if="metaField.type == 'prefilled' && prefillData[metaField.value] && Array.isArray(prefillData[metaField.value])">
                  <p v-for="boss in prefillData[metaField.value]" :key="boss" class="body-1 mb-0">{{ boss }}</p>
                </template>
                <p v-else-if="metaField.type == 'fixed' && metaField.value" class="body-1 mb-0">{{ metaField.value }}</p>
                <p v-else class="body-1 mb-0">-</p>
              </section>
            </div>
          </v-row>
          <v-row v-else>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('employee.moduleTitle') }}</p>
                <p class="body-1 mb-0">{{ currentUser.firstname }} {{ currentUser.lastname }}</p>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('employeenr') }}</p>
                <p v-if="currentUser.employeenr" class="body-1 mb-0">{{ currentUser.employeenr }}</p>
                <p v-else class="body-1 mb-0">-</p>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('boss') }}</p>
                <p v-if="bosses.length === 0" class="body-1 mb-0">-</p>
                <template v-else>
                  <p v-for="boss in bosses" :key="boss" class="body-1 mb-0">{{ boss }}</p>
                </template>
              </section>
            </div>
          </v-row>
          <v-row>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('wb-config.infos.datum.title') }}</p>
                <v-menu
                  v-model="datePickerMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  z-index="999"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFormatted"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      :background-color="textFieldClass"
                      class="rounded-lg"
                      hide-details
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="date" first-day-of-week="1" @change="updateStoreAndLocalStorage" @input="datePickerMenu = false"></v-date-picker>
                </v-menu>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('bf.timeFrom') }}</p>
                <v-menu v-model="timePickerFromMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="timeFrom"
                      prepend-inner-icon="mdi-clock-time-four-outline"
                      readonly
                      :background-color="textFieldClass"
                      class="rounded-lg"
                      hide-details
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    v-model="timeFrom"
                    @input="updateStoreAndLocalStorage"
                    @click:minute="timePickerFromMenu = false"
                    format="24hr"
                  ></v-time-picker>
                </v-menu>
              </section>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-4">
              <section :class="sectionClass">
                <p class="caption">{{ $t('bf.timeTo') }}</p>
                <v-menu
                  v-model="timePickerToMenu"
                  :close-on-content-click="false"
                  :nudge-left="nudgeTimePicker()"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="timeTo"
                      prepend-inner-icon="mdi-clock-time-five-outline"
                      readonly
                      :background-color="textFieldClass"
                      class="rounded-lg"
                      hide-details="auto"
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    v-model="timeTo"
                    @input="updateStoreAndLocalStorage"
                    @click:minute="timePickerToMenu = false"
                    format="24hr"
                    :min="timeFrom"
                  ></v-time-picker>
                </v-menu>
              </section>
            </div>
          </v-row>
          <v-row class="basisitem" v-for="additionalField in additionalFields" :key="`additional-field-${additionalField.id}`">
            <v-col>
              <section v-if="additionalField.type === additionalFieldsType.text" :class="sectionClass">
                <v-text-field
                  v-model="questionnaireData.baseData.additionalFields[additionalField.id].value"
                  @change="updateStoreAndLocalStorage"
                  :label="getAdditionalFieldLabel(additionalField)"
                  :required="additionalField.mandatory === 1"
                  :background-color="textFieldClass"
                  class="rounded-lg"
                  hide-details="auto"
                  outlined
                />
              </section>
              <section v-if="additionalField.type === additionalFieldsType.segmented" :class="sectionClass">
                <p class="body-1">{{ getAdditionalFieldLabel(additionalField) }}</p>
                <v-btn-toggle
                  v-model="questionnaireData.baseData.additionalFields[additionalField.id].value"
                  active-class="primary"
                  :color="textFieldClass"
                  rounded
                >
                  <v-btn
                    v-for="(segmentedItem, idx) in additionalField.values.split(',')"
                    :key="`${additionalField.id}-${idx}`"
                    :value="segmentedItem"
                    @change="updateStoreAndLocalStorage"
                  >
                    {{ segmentedItem }}
                  </v-btn>
                </v-btn-toggle>
              </section>
            </v-col>
          </v-row>
          <!-- <pre v-if="debug"><code>{{ JSON.stringify(questionnaireData,null,2) }}</code></pre> -->

          <!-- RATINGS (Begleitfahrten fertige Formulare zum auswählen) -->
          <v-row class="basisitem" v-if="showRatings">
            <v-col>
              <section :class="sectionClass">
                <p class="caption">{{ $t('bf.accompaniments') }} ({{ $t('bf.averageOfAllAccompaniments') }})</p>
                <div class="body-1 mb-0">
                  <table width="100%">
                    <thead>
                      <tr>
                        <th class="text-left">&nbsp;</th>
                        <th class="text-left">{{ $t('bf.accompaniments.date') }}</th>
                        <th class="text-left">{{ $t('bf.accompaniments.time') }}</th>
                        <th class="text-left">{{ $t('bf.accompaniments.accompaniment') }}</th>
                        <th class="text-right">{{ $t('bf.accompaniments.grade') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in ratings" :key="item.name">
                        <td>
                          <v-switch
                            v-model="questionnaireData.baseData.questionnaires"
                            :disabled="isNaN(item.grade)"
                            color="primary"
                            label=""
                            :value="item.id"
                            hide-details
                            style="margin-top: 8px; margin-bottom: 8px; padding-top: 0"
                            @change="updateStoreAndLocalStorage"
                          ></v-switch>
                        </td>
                        <td>{{ item.date }}</td>
                        <td>{{ item.time }}</td>
                        <td>{{ item.title }}</td>
                        <td class="text-right">{{ item.grade }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </v-col>
          </v-row>
        </div>

        <!-- NOTE (Bemerkung) -->
        <div v-else-if="selectedTopicId === 'note'" class="pa-7 note-container">
          <div class="col">
            <section :class="sectionClass">
              <v-textarea
                v-model="questionnaireData.note"
                class="rounded-lg"
                :background-color="textFieldClass"
                rows="4"
                hide-details="auto"
                no-resize
                auto-grow
                outlined
                label="Bemerkung"
                @change="updateStoreAndLocalStorage"
              ></v-textarea>
            </section>
          </div>
        </div>

        <!-- Auswertung -->
        <div v-else-if="selectedTopicId === 'review'" class="chart-container pa-7 d-flex flex-column align-center">
          <h1 :class="{ 'text-h6': smAndDown }" class="mb-4">{{ questionnaire.title }}</h1>
          <begleitfahrt-resultat-chart
            v-if="chartData.data.length > 0"
            class="chart-component"
            :chart-options="chartOptions"
            :chart-labels="chartData.labels"
            :chart-colors="chartData.colors"
            :chart-data="chartData.data"
          ></begleitfahrt-resultat-chart>
          <span v-else>Keine Bewertungen zum Anzeigen gefunden</span>
        </div>

        <!-- COMPLETE (Abschliessen) -->
        <div v-else-if="selectedTopicId === 'complete'" class="pa-7 basedata-container d-flex flex-column">
          <section :class="sectionClass" class="d-flex flex-row justify-space-between align-center">
            <p class="body-1 ma-0">{{ $t('begleitfahrt.complete.text') }}</p>
            <v-switch v-model="switchStateCompleted" color="primary" label="" hide-details style="margin-top: 8px; margin-bottom: 8px; padding-top: 0">
            </v-switch>
          </section>
          <div class="d-flex flex-row justify-end">
            <v-btn class="mx-4 mt-4" color="secondary" :disabled="!isCompletedSwitch || !isCompleted()" @click="saveBegleitfahrt()">
              {{ $t('begleitfahrt.complete') }}
            </v-btn>
          </div>
          <div class="d-flex flex-row justify-end">
            <p v-if="!isCompleted()" class="error--text caption mx-4 mt-1">{{ $t('begleitfahrt.complete.warning') }}</p>
          </div>
        </div>

        <!-- OVERALL GRADE (Gesamtnote) -->
        <OverallGrade
          v-else-if="selectedTopicId === 'overallGrade'"
          class="pa-7"
          :sections="questionnaire.sections"
          :selectedBaseDataQuestionnaires="questionnaireData.baseData.questionnaires"
          :baseDataRatings="ratings"
          :localTopics="currentLocalTopics"
        ></OverallGrade>
      </template>
    </responsive-drawer-layout>
    <v-bottom-sheet v-model="showBottomSheet" inset>
      <drawer-mobile
        @onSelectedTopic="selectTopic"
        v-model="questionnaireData"
        :selectedTopicId="String(selectedTopicId)"
        :questionnaire="questionnaire"
        :evaluationType="evaluationType"
      >
      </drawer-mobile>
    </v-bottom-sheet>
    <floating-drawer-button @onSelectedFloatingButton="showBottomSheet = !showBottomSheet" />
  </div>
</template>

<script>
import BegleitfahrtResultatChart from '@/modules/begleitfahrt/components/BegleitfahrtResultatChart.vue';
import common from '../../../mixins/common';
import dateUtils from '../../../common/dateUtils';
import DrawerDesktop from '../components/DrawerDesktop.vue';
import DrawerMobile from '../components/BottomSheetTopicNavigation.vue';
import FloatingDrawerButton from '../components/FloatingNavigationButton.vue';
import { mapActions, mapState } from 'vuex';
import OverallGrade from './OverallGrade.vue';
import { preprocessImage } from '@/common/mediaUtils';
import ResponsiveDrawerLayout from '../../../layouts/ResponsiveDrawerLayout.vue';
import configMixin from '@/mixins/configMixin';
import config from '@/common/config';
import { debounce } from '@/common/utils';

export default {
  name: 'BegleitfahrtBewertungFormular',
  components: { BegleitfahrtResultatChart, DrawerDesktop, DrawerMobile, OverallGrade, ResponsiveDrawerLayout, FloatingDrawerButton },
  mixins: [common, configMixin],
  data: () => ({
    galleryCameraSwitchEnabled: true, // for the time being this is a local flag, to enable the user to switch between camera and gallery. Might be configurable in future by tenant / usage
    showBottomSheet: false,
    selectedTopicId: 'basedata',
    date: dateUtils.formatDateAsYYYYMMDD(new Date()),
    timeFrom: dateUtils.formatTime(new Date()),
    timeTo: dateUtils.formatTime(new Date()),
    datePickerMenu: false,
    timePickerFromMenu: false,
    timePickerToMenu: false,
    questionnaireData: {
      baseData: {
        date: dateUtils.formatDateAsYYYYMMDD(new Date()),
        timeFrom: dateUtils.formatTime(new Date()),
        timeTo: dateUtils.formatTime(new Date()),
        completed: false,
        additionalFields: {},
        questionnaires: [],
      },
      topics: {},
      note: '',
      evaluationDate: '',
      evaluationReportIds: '',
    },
    chartOptions: {
      chart: {
        type: 'donut',
        width: '100%',
        height: '100%',
        foreColor: '', // Has to be set to overwrite the default color for it to use the global color
        animations: {
          speed: 500,
        },
      },
    },
    showAlert: false,
    evaluationType: 'evaluation',
    additionalFieldsType: {
      segmented: 'segmented',
      text: 'text',
    },
    switchStateCompleted: false,
    topicSavedPositions: {},
    handleDebouncedScroll: null,
  }),
  mounted() {
    setTimeout(() => {
      this.showAlert = true;
      console.warn('123', dateUtils.formatDateTime(new Date()));
    }, 1000);
    this.handleDebouncedScroll = debounce(this.handleQuestionScroll, 300);
    setTimeout(() => {
      this.listenToScroll();
    }, 250);
  },
  beforeDestroy() {
    const scrollContainer = this.$refs['questions-container'] ? this.$refs['questions-container'] : this.$refs['basedata-container'];
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', this.handleDebouncedScroll);
    }
  },
  methods: {
    ...mapActions('begleitfahrt', ['saveQuestionnaireLocalData', 'deleteQuestionnaireDataInStateAndStorage']),
    nudgeTimePicker() {
      if (this.lg) {
        return 50;
      } else if (this.md) {
        return 120;
      }
      return 0;
    },
    selectTopic(event, topicId) {
      this.selectedTopicId = topicId;
      // Save the new topic also on the vuex store
      this.updateStoreAndLocalStorage();
      // Navigate to new route
      this.$router.push(
        `/begleitfahrt/${this.mx_instance}/bewertung/${this.currentUserId}/${this.currentQuestionnaireId}/${this.currentQuestionnaireDataUuid}/${topicId}`
      );
      this.showBottomSheet = false;
    },
    /**
     * Update to vuex store
     * and save backup to local-storage
     */
    updateStoreAndLocalStorage() {
      /**
       * Prepare date-/time-picker values
       */
      this.questionnaireData.baseData.date = this.date;
      this.questionnaireData.baseData.timeFrom = this.timeFrom;
      if (parseInt(this.timeTo.replace(':', '')) < parseInt(this.timeFrom.replace(':', ''))) {
        /** Time from is newer than time to -> adapte time to match time from */
        this.timeTo = this.timeFrom;
      }
      this.questionnaireData.baseData.timeTo = this.timeTo;

      /**
       * Update to vuex store
       * and save backup to local-storage
       */
      this.saveQuestionnaireLocalData({
        questionnaireLocalData: {
          ...this.questionnaireData,
          questionnaireId: this.currentQuestionnaireId,
          selectedTopicId: this.selectedTopicId,
        },
        userId: this.currentUserId,
        uuid: this.currentQuestionnaireDataUuid,
      });
    },
    pageHistoryBack() {
      this.$router.go(-1);
    },
    uploadFile() {
      //console.debug('ref fileinputref ', this.$refs.fileInputRef.children);
      /*
      const fileInputComponent = this.$refs.fileInputRef;
      if (fileInputComponent) {
        // Attempt to find the native input within the Vuetify component
        const input = fileInputComponent.$refs.input; // Accessing the internal reference
        if (input) {
          input.click(); // This triggers the file input dialog
        } else {
          console.error('File input not found');
        }
      }*/
    },
    async addImagePreview(questionId, images) {
      for (const image of images) {
        if (image.type.startsWith('image/')) {
          const base64Image = await preprocessImage(image);
          let previews = this.questionnaireData.topics[this.selectedTopicId].questions[questionId].images || [];
          previews.push(base64Image);
          this.$set(this.questionnaireData.topics[this.selectedTopicId].questions[questionId], 'images', previews);
        }
      }
      this.updateStoreAndLocalStorage();
    },
    removeImg(questionId, index) {
      this.questionnaireData.topics[this.selectedTopicId].questions[questionId].images.splice(index, 1);
    },
    /*
    async saveImageInObject(questionId) {
      const image = this.questionnaireData.topics[this.selectedTopicId].questions[questionId].image
      if (image && image.size > 0) {
        const base64Image = await preprocessImage(image);
        console.debug('?!?!', base64Image)
        this.$set(this.questionnaireData.topics[this.selectedTopicId].questions[questionId], 'image', base64Image);
      }
    },*/
    getAdditionalFieldLabel(additionalField) {
      return additionalField.mandatory === 1 ? additionalField.title + ' *' : additionalField.title;
    },
    existsInStore() {
      if (!this.questionnaireLocalData[this.currentUserId]) {
        /** No questionnaire-local-data for this user -> return false */
        // console.error('user not found');
        return false;
      }
      if (!this.questionnaireLocalData[this.currentUserId][this.currentQuestionnaireDataUuid]) {
        /** No questionnaire-local-data for this uuid -> return false */
        // console.error('uuid not found');
        return false;
      }
      /** Found questionnaire-local-data for this user and uuid -> return true */
      return true;
    },
    loadFromStore() {
      this.questionnaireData = this.questionnaireLocalData[this.currentUserId][this.currentQuestionnaireDataUuid];
      this.date = this.questionnaireData['baseData']['date'];
      this.timeFrom = this.questionnaireData['baseData']['timeFrom'];
      this.timeTo = this.questionnaireData['baseData']['timeTo'];
      this.selectedTopicId = this.questionnaireData['selectedTopicId'];
    },
    deleteFromLocalStorage() {
      const userId = this.currentUserId;
      const uuid = this.currentQuestionnaireDataUuid;
      this.$store.dispatch('begleitfahrt/deleteQuestionnaireDataInStateAndStorage', { userId, uuid });
    },
    isSelectedTopicIdNumeric() {
      return typeof this.selectedTopicId === 'number';
    },
    extractAnswers(questionnaireData) {
      const self = this; // capture the Vue instance if you need to access other methods or data

      function extractNestedAnswers(obj) {
        return Object.entries(obj).reduce((acc, [key, value]) => {
          if (key === 'answer' && value !== '') {
            acc.push(value);
          } else if (value && typeof value === 'object') {
            acc.push(...extractNestedAnswers(value)); // now using the correctly scoped function
          }
          return acc;
        }, []);
      }

      return extractNestedAnswers(questionnaireData);
    },
    areFiveOrLessAnswers(question) {
      return this.answerSetsById[question.answerSetIdFk].answers.length + (this.answerSetsById[question.answerSetIdFk].norating ? 1 : 0) <= 5;
    },
    hasNoRatingOption(question) {
      return this.answerSetsById[question.answerSetIdFk].norating;
    },
    hasCommentArea(question) {
      return !!question.onlyComment;
    },
    canAddMedias(question) {
      return this.answerSetsById[question.answerSetIdFk].canAddMedias;
    },
    isCompleted() {
      for (const field of Object.values(this.questionnaireData.baseData.additionalFields)) {
        if (field.required && !field.value) {
          return false;
        }
      }
      for (const [topic] of Object.entries(this.questionnaireData.topics)) {
        if (!this.questionnaireData.topics[topic].completed) {
          return false;
        }
      }
      return true;
    },
    async saveBegleitfahrt() {
      let evaluationReportIds;
      const selectedQuestionnairesIds = this.questionnaireData.baseData.questionnaires;

      if (this.evaluationType === 'evaluation') {
        evaluationReportIds = this.rating
          .filter((r) => selectedQuestionnairesIds.includes(r.id))
          .map((r) => r.uuid)
          .join(',');

        this.questionnaireData.evaluationReportIds = evaluationReportIds;
        this.questionnaireData.evaluationDate = new Date().toISOString();
      }

      await this.$store.dispatch('begleitfahrt/storeBegleitfahrt', {
        questionnaireData: JSON.stringify(this.questionnaireData),
        questionnaireId: this.currentQuestionnaireId,
        userId: this.currentUserId,
        teamleaderId: this.currentTeamLeader, // TODO: get teamleaderId
        uuid: this.currentQuestionnaireDataUuid,
      });
      this.deleteFromLocalStorage();
      await this.$router.push(`/begleitfahrt/${this.mx_instance}/${this.currentUserId}`);

      await this.$store.dispatch('sync/invalidateModuleCacheAndSync', { moduleId: 'begleitfahrt' }, { root: true });
    },
    additionalInfoIsRequired(questionId) {
      try {
        if (
          this.questionnaireData.topics[this.selectedTopicId].questions[questionId].answer &&
          this.answerById[this.questionnaireData.topics[this.selectedTopicId].questions[questionId].answer].additionalInfoRequired &&
          !this.questionnaireData.topics[this.selectedTopicId].questions[questionId].comment
        ) {
          return true;
        }
      } catch (err) {
        console.warn('additionalInfoIsRequired failed', err);
      }
      return false;
    },
    listenToScroll() {
      const scrollContainer = this.$refs['questions-container'] ? this.$refs['questions-container'] : this.$refs['basedata-container'];
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', this.handleDebouncedScroll);
        scrollContainer.addEventListener('scroll', this.handleDebouncedScroll);

        const topicId = this.questionnaireData.selectedTopicId;
        if (topicId && this.topicSavedPositions[topicId.toString()]) {
          scrollContainer.scrollTo({
            top: this.topicSavedPositions[topicId.toString()],
            behavior: 'smooth',
          });
        } else {
          scrollContainer.scrollTo({
            top: 0,
          });
        }
      }
    },
    handleQuestionScroll(event) {
      const topicId = this.questionnaireData.selectedTopicId;

      if (topicId) {
        this.topicSavedPositions[topicId.toString()] = event.target.scrollTop;
      }
    },
  },
  computed: {
    ...mapState({
      user: (state) => state.profile.user,
    }),
    ...mapState('begleitfahrt', [
      'additionalFieldById',
      'answerSetsById',
      'answerById',
      'questionnaireById',
      'questionnaireQuestionById',
      'questionnaireAnswerDescription',
      'userById',
      'deviceUserBoss',
      'questionnaireLocalData',
      'rating',
      'config',
      'prefillDataByUserId',
    ]),
    isMobile() {
      return config.browserInfo.mobile;
    },
    moduleConfig() {
      return this.getModuleConfig(this.$route.meta.moduleType, this.$route.params.instance);
    },
    currentQuestionnaireId() {
      return this.$route.params.questionnaireId ? this.$route.params.questionnaireId : 0;
    },
    currentQuestionnaireDataUuid() {
      return this.$route.params.uuid ? this.$route.params.uuid : 0;
    },
    currentUserId() {
      return this.$route.params.userId ? parseInt(this.$route.params.userId.toString()) : 0;
    },
    currentTeamLeader() {
      // TODO: userId not properly set?
      return this.user.userId ? this.user.userId : undefined; // current logged in user conducting the begleitfahrt
    },
    prefillData() {
      return this.prefillDataByUserId[this.currentUserId] ? this.prefillDataByUserId[this.currentUserId] : {};
    },
    currentTopic() {
      return this.$route.params.topicId ? this.$route.params.topicId.toString() : 'basedata';
    },
    currentUser() {
      return this.userById[this.currentUserId] ? this.userById[this.currentUserId] : { id: 0, firstname: '-', lastname: '', employeenr: '-' };
    },
    questionnaire() {
      return this.questionnaireById[this.currentQuestionnaireId];
    },
    currentLocalTopics() {
      return this.questionnaireLocalData[this.currentUserId][this.currentQuestionnaireDataUuid].topics;
    },
    ratings() {
      return this.rating
        .filter((r) => r.userIdFk === this.currentUserId && r.isType && r.isType === 'questionnaire')
        .sort((a, b) => new Date(b.clientdate) - new Date(a.clientdate))
        .map((r) => {
          const questionnaire = this.questionnaireById[r.questionnaireIdFk];
          const title = questionnaire.title ? questionnaire.title : '';
          return {
            id: r.id,
            title: title,
            date: dateUtils.reformatDateIsoStringAsYYYYMMDD(r.clientdate, false),
            time: dateUtils.reformatDateIsoStringAsHHmm(r.clientdate),
            grade: r.grade ? r.grade.toFixed(2) : '-',
          };
        });
    },
    filteredQuestions() {
      if (this.selectedTopicId && this.questionnaireQuestionById && this.questionnaire) {
        let questions = Object.values(this.questionnaireQuestionById).filter((question) => question.topicIdFk === this.selectedTopicId);
        return questions.sort((a, b) => a.sort - b.sort);
      }
      return [];
    },
    additionalFields() {
      return Object.values(this.additionalFieldById)
        .filter((f) => {
          return !this.questionnaire.hiddenAdditionalFieldIds.includes(f.id);
        })
        .sort((a, b) => a.sort - b.sort);
    },
    getDescription: function () {
      // Returns a method that gets the description for a given questionId
      return (questionId) => {
        let answerId = this.questionnaireData.topics[this.selectedTopicId].questions[questionId].answer;
        if (answerId) {
          let description = this.questionnaireAnswerDescription.find((desc) => {
            return desc.questionIdFk === questionId && desc.answerIdFk === answerId;
          });
          return description ? description.description : '';
        }
        return '';
      };
    },
    dateFormatted() {
      const parts = this.date.split('-');
      return parts[2] + '.' + parts[1] + '.' + parts[0];
    },
    chartData() {
      const questionnaireData = this.questionnaireData.topics;
      const questionnaireAnswers = this.extractAnswers(questionnaireData);
      let answerCounts = {};
      let labelToColor = {};

      questionnaireAnswers.forEach((a) => {
        const answer = this.answerById[a];
        if (answer && answer.answer) {
          const answerValue = answer.answer;
          answerCounts[answerValue] = (answerCounts[answerValue] || 0) + 1;

          if (!labelToColor[answerValue] && answer.color) {
            labelToColor[answerValue] = answer.color;
          }
        }
      });
      const labels = Object.keys(answerCounts);
      const data = Object.values(answerCounts);
      const colors = labels.map((label) => labelToColor[label] || '');

      return {
        labels: labels,
        colors: colors,
        data: data,
      };
    },
    showRatings() {
      return this.questionnaire && this.questionnaire.isType && this.questionnaire.isType === this.evaluationType && this.ratings.length > 0;
    },
    sectionClass() {
      return this.darkMode ? 'pa-4 grey darken-3 rounded-lg' : 'pa-4 grey lighten-3 rounded-lg';
    },
    textFieldClass() {
      return this.darkMode ? '' : 'grey lighten-5';
    },
    isCompletedSwitch() {
      return this.switchStateCompleted;
    },
    bosses() {
      const bossIds = this.deviceUserBoss.filter((boss) => boss.userId === this.currentUserId);
      return bossIds
        .filter((boss) => this.userById[boss.bossId] !== undefined)
        .map((boss) => this.userById[boss.bossId].firstname + ' ' + this.userById[boss.bossId].lastname);
    },
    metaFields() {
      if (Array.isArray(this.config) && this.config.length > 0) {
        if (this.config[0].jsonValue && this.config[0].jsonValue.metaFields) {
          return this.config[0].jsonValue.metaFields;
        }
      }
      return [];
    },
  },

  watch: {
    questionnaire: {
      handler(newQuestionnaire) {
        if (this.existsInStore()) {
          this.loadFromStore();
        } else {
          for (let additionalField of this.additionalFields) {
            this.$set(this.questionnaireData.baseData.additionalFields, additionalField.id, {
              value: additionalField.defaultvalue,
              required: additionalField.mandatory,
            });
          }
          if (this.questionnaire) {
            this.$set(this.questionnaireData, 'isType', this.questionnaire.isType);
            if (this.questionnaire.sections) {
              for (let section of this.questionnaire.sections) {
                for (let topic of section.topics) {
                  this.$set(this.questionnaireData.topics, topic.id, {
                    completed: false,
                    questions: {},
                  });
                  let questions = Object.values(this.questionnaireQuestionById)
                    .filter((question) => question.topicIdFk === topic.id)
                    .sort((a, b) => a.sort - b.sort);
                  for (let question of questions) {
                    this.$set(this.questionnaireData.topics[topic.id].questions, question.id, {
                      answer: question.answerSetIdFk === 0 ? '0' : '',
                      comment: '',
                    });
                  }
                }
              }
            }
          }
        }
      },
      immediate: true,
    },
    questionnaireData: {
      handler(newData, prevData) {
        let chartData = [];
        // check additional fields for completion
        let completed = true;
        for (let additionalField of Object.values(newData.baseData.additionalFields)) {
          if (additionalField.required === 1 && !additionalField.value) {
            completed = false;
            break;
          }
        }
        this.questionnaireData.baseData.completed = completed;
        // check topics for completion
        for (const [topicId, topic] of Object.entries(newData.topics)) {
          completed = true;
          for (let question of Object.values(newData.topics[topicId].questions)) {
            if (!question.answer) {
              completed = false;
              break;
            } else {
              if (question.answer) {
                if (this.answerById[question.answer] && this.answerById[question.answer].additionalInfoRequired && !question.comment) {
                  completed = false;
                  break;
                }
                chartData.push(question.answer);
              }
            }
          }
          this.questionnaireData.topics[topicId].completed = completed;
        }
        // save to local storage
        // this.saveToLocalStorage();
        if (newData.selectedTopicId !== prevData.selectedTopicId) {
          setTimeout(() => {
            this.listenToScroll();
          }, 250);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.questions-container,
.basedata-container,
.note-container {
  position: fixed;
  box-sizing: border-box;
  max-height: 100%;
  width: 100%;
  overflow-y: auto;
}

@media only screen and (min-width: 1100px) {
  .questions-container,
  .basedata-container,
  .note-container {
    width: calc(100% - 450px);
  }
}

@media only screen and (min-width: 1904px) {
  .questions-container,
  .basedata-container,
  .note-container {
    width: calc(100% - 516px);
  }
}

.headline-container {
  margin: 0 3% 0 3%;
}

.active-topic:before {
  border-radius: 10px;
}

.img-preview-container {
  width: 80px;
  height: 80px;
}

.file-input-outer-container {
  padding: 0 8px 16px 8px;
  width: 80px;
}

.file-input-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  margin: 0 8px 0 0;
  padding: 0;

  border-radius: 10px;
  border: 1px grey solid;
}

.file-input {
  margin: 0;
  padding: 0 0 0 30%;
}

.img-preview {
  border-radius: 10px;
}

.preview-delete-icon {
  padding-bottom: 10%;
}

.preview-delete-icon:hover {
  cursor: pointer;
}

.img-overlay {
  left: auto;
  width: 20px;
  height: 20px;
  background-color: rgba(255, 0, 0, 0.6);
  border-radius: 50%;
}

.fade-enter-active,
.fade-leave-active {
  /*animation: bounce-in-top 1s both;*/
  transition: opacity 1s ease-in-out;
}

.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}

.alert-container {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.alert-container p {
  margin: 0;
}

.chart-container {
  height: 50dvh;
}

.chart-component {
  height: 100%;
  width: 100%;
}
</style>

<style type="text/css">
.v-textarea.isRequired fieldset {
  border: 1px solid red !important;
}
</style>
